import { useState, useRef, useCallback } from "react";
import { Img } from "@/components/ui/img";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { uploadFile } from "@/lib/upload";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { Button } from "./button";
import { Input } from "./input";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import {
  ImageSquare,
  CircleNotch,
  X,
  Upload,
  Image as ImageIcon,
  FileText,
  FilmSlate,
  MagnifyingGlass,
  Books,
} from "@phosphor-icons/react";

interface MediaItem {
  id: string;
  originalName: string;
  folder: string;
  mimeType: string;
  size: number;
  urls: Record<string, string>;
  uploaderName: string | null;
  createdAt: string;
}

interface MediaPickerProps {
  value: string;
  onChange: (url: string) => void;
  folder: string;
  accept?: string;
  label?: string;
  hint?: string;
  previewHeight?: string;
  disabled?: boolean;
  /** Hide the upload button – users can only pick from the media library */
  hideUpload?: boolean;
  /** Maximum allowed video duration in seconds (only applies to video uploads) */
  maxVideoDuration?: number;
}

export function MediaPicker({
  value,
  onChange,
  folder,
  accept = "image/jpeg,image/png,image/webp",
  label,
  hint = "JPG, PNG ili WebP · maks. 5 MB",
  previewHeight = "h-44",
  disabled = false,
  hideUpload = false,
  maxVideoDuration,
}: MediaPickerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useRef(`media-picker-${crypto.randomUUID().slice(0, 8)}`).current;

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setError(null);

      // Client-side video duration check
      if (maxVideoDuration && file.type.startsWith("video/")) {
        try {
          const duration = await getVideoDuration(file);
          if (duration > maxVideoDuration) {
            const msg = `Video je predug (${Math.round(duration)}s). Maksimalno dozvoljeno: ${maxVideoDuration}s.`;
            setError(msg);
            toast.error(msg);
            if (inputRef.current) inputRef.current.value = "";
            return;
          }
        } catch {
          // If we can't read duration, let the server-side check handle it
        }
      }

      setIsUploading(true);
      try {
        const result = await uploadFile(file, folder);
        onChange(result.fileUrl);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Greška pri uploadu.";
        setError(msg);
        toast.error(msg);
      } finally {
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [folder, onChange, maxVideoDuration]
  );

  const handleSelectFromLibrary = useCallback(
    (url: string) => {
      onChange(url);
      setLibraryOpen(false);
    },
    [onChange]
  );

  const isImage = accept?.includes("image") ?? true;

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-sm font-medium text-foreground">{label}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        id={inputId}
      />

      {value ? (
        <div className="group relative overflow-hidden rounded-xl border">
          {isImage ? (
            <Img
              src={value}
              alt="Pregled"
              className={cn("w-full object-cover", previewHeight)}
            />
          ) : (
            <div className={cn("flex items-center justify-center bg-muted/20", previewHeight)}>
              <FileText className="size-12 text-muted-foreground/30" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100">
            {!hideUpload && (
              <label
                htmlFor={inputId}
                className="cursor-pointer rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
              >
                Upload novi
              </label>
            )}
            <button
              type="button"
              onClick={() => setLibraryOpen(true)}
              className="rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
            >
              Biblioteka
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="rounded-lg bg-white/90 p-1.5 text-rose-600 shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-10 transition-colors",
            isUploading
              ? "pointer-events-none border-border bg-muted/30"
              : disabled
                ? "pointer-events-none border-border/40 bg-muted/20"
                : "border-border/60"
          )}
        >
          {isUploading ? (
            <>
              <CircleNotch className="size-8 animate-spin text-primary/40" />
              <p className="text-sm font-medium text-muted-foreground">Uploadovanje...</p>
            </>
          ) : (
            <>
              <ImageSquare className="size-8 text-muted-foreground/40" />
              <div className="flex gap-2">
                {!hideUpload && (
                  <label
                    htmlFor={inputId}
                    className="cursor-pointer rounded-lg border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
                  >
                    <Upload className="mr-1 inline size-3.5" />
                    Upload
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setLibraryOpen(true)}
                  className="rounded-lg border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
                >
                  <Books className="mr-1 inline size-3.5" />
                  Biblioteka
                </button>
              </div>
              {hint && (
                <p className="text-xs text-muted-foreground/60">{hint}</p>
              )}
            </>
          )}
        </div>
      )}

      {error && <p className="text-xs text-rose-600">{error}</p>}

      {libraryOpen && (
        <MediaLibraryDialog
          folder={folder}
          onSelect={handleSelectFromLibrary}
          onClose={() => setLibraryOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── Media Library Dialog ─── */

export function MediaLibraryDialog({
  folder,
  onSelect,
  onClose,
}: {
  folder: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["media-picker", folder, search, page],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("folder", folder);
      if (search) sp.set("search", search);
      sp.set("page", String(page));
      sp.set("limit", "18");
      const res = await fetch(`/api/media?${sp}`);
      await throwIfNotOk(res, "Greška pri učitavanju medija");
      return res.json() as Promise<{ data: MediaItem[]; total: number }>;
    },
  });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 18);

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <DialogTitle>Medijska biblioteka</DialogTitle>
        <DialogDescription>Odaberite postojeći fajl iz biblioteke.</DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pretraži..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10"
            />
          </div>

          {isLoading ? (
            <div className="grid grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageIcon className="size-10 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">Nema medija u ovom folderu.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {items.map((item) => {
                const isImage = item.mimeType.startsWith("image/");
                const isVideo = item.mimeType.startsWith("video/");
                const thumbUrl = item.urls.xsmall || item.urls.small || item.urls.full;
                const selectUrl = item.urls.full || item.urls.original || Object.values(item.urls)[0];

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(selectUrl)}
                    className="group relative aspect-square overflow-hidden rounded-lg border bg-muted/20 transition-all hover:ring-2 hover:ring-primary"
                  >
                    {isImage && thumbUrl ? (
                      <Img
                        src={thumbUrl}
                        alt={item.originalName}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        {isVideo ? (
                          <FilmSlate className="size-8 text-muted-foreground/30" />
                        ) : (
                          <FileText className="size-8 text-muted-foreground/30" />
                        )}
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-4">
                      <p className="truncate text-[10px] font-medium text-white">
                        {item.originalName}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Prethodna
              </Button>
              <span className="text-xs text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Sljedeća
              </Button>
            </div>
          )}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Otkaži
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };
    video.src = URL.createObjectURL(file);
  });
}
